/* eslint-disable react/no-unescaped-entities */
import { Button, Input } from '@chakra-ui/react'
import { Gitlab } from '@gitbeaker/rest'
import { DataProvider } from '@nartex/api-platform'
import { useEvent } from '@nartex/react-libs'
import { once } from '@nartex/stdlib'
import { useState, useMemo, useRef, useEffect } from 'react'
import type { PropsWithChildren } from 'react'
import { antiCorruptionLayerProxy } from 'src/adapters/antiCorruptionLayerProxy'
import { useEnv } from 'src/adapters/Env'
import {
  HTTPClient,
  HttpClientContext,
  CockpitHTTPClient,
  DataProviderContext,
} from 'src/adapters/HTTPClient'
import './styles.css'
import { useStorage } from 'src/adapters/Storage'
import { Version } from 'src/Version'

import { GitlabClientProvider } from '../adapters/GitlabClient'
import { IdentityProvider } from '../adapters/Identity'

const TOKEN_STORAGE_KEY = 'nxcalendar-gitlab-api-token'

export function ApiTokenProvider(props: PropsWithChildren<object>) {
  const { children } = props
  const env = useEnv()

  const [apiToken, setToken] = useStoredToken()
  const [isValid, setIsValid] = useState(true)

  const onSetToken = useEvent((token: string) => {
    setToken(token)
    setIsValid(true)
  })

  const onInvalidCredentials = useMemo(() => {
    return once(() => {
      if (isValid) {
        alert('Votre jeton gitlab semble invalide')
        setIsValid(false)
      }
    })
  }, [isValid])

  const httpClient = useMemo(
    () =>
      HTTPClient({
        baseURL: env.API_URL,
        getAccessToken: () => apiToken,
        onInvalidCredentials,
      }),

    [env.API_URL, onInvalidCredentials, apiToken],
  )

  const gitlabClient = useMemo(() => {
    return new Gitlab({
      host: env.API_URL.replace(/\/api\/v4$/, ''),
      token: apiToken,
    })
  }, [apiToken, env.API_URL])

  const dataProvider = useMemo(() => {
    const cockpitHttpClient = CockpitHTTPClient({
      getAccessToken: () => apiToken,
      baseURL: env.COCKPIT_URL,
      auth: { username: env.COCKPIT_LOGIN, password: env.COCKPIT_JWT },
    })
    return antiCorruptionLayerProxy(
      // @ts-expect-error just a type mismatch following a migration
      DataProvider({ httpClient: cockpitHttpClient, apiUrl: env.COCKPIT_URL }),
    )
  }, [env.COCKPIT_URL, env.COCKPIT_LOGIN, env.COCKPIT_JWT, apiToken])

  if (apiToken && isValid) {
    return (
      <DataProviderContext.Provider value={dataProvider}>
        <HttpClientContext.Provider value={httpClient}>
          <GitlabClientProvider value={gitlabClient}>
            <IdentityProvider>{children}</IdentityProvider>
          </GitlabClientProvider>
        </HttpClientContext.Provider>
      </DataProviderContext.Provider>
    )
  }

  return (
    <section className="apitoken-prompt">
      <Onboarding onSubmit={onSetToken} />

      <Version className="version" />
    </section>
  )
}

function useStoredToken() {
  const storage = useStorage()

  const [apiToken, setToken] = useState(() => {
    return storage.getItem(TOKEN_STORAGE_KEY) || ''
  })

  useEffect(() => {
    storage.setItem(TOKEN_STORAGE_KEY, apiToken)
  }, [storage, apiToken])

  return [apiToken, setToken] as const
}

type OnboardingProps = {
  onSubmit: (token: string) => void
}

function Onboarding(props: OnboardingProps) {
  const { onSubmit } = props
  const inputRef = useRef<HTMLInputElement>(null)

  const [hasClicked, setCLicked] = useState(false)

  if (!hasClicked) {
    return (
      <div className="content">
        <h2>Bienvenue</h2>
        <p>Pour vous connecter, merci de renseigner votre token Gitlab</p>
        <a
          href="https://projects.nartex.fr/-/user_settings/personal_access_tokens"
          target="_blank"
          rel="noreferrer"
          className="button-element"
          onClick={() => setCLicked(true)}
        >
          Générer un token avec le scope "API"
        </a>
        <Button
          className="button-as-link"
          type="button"
          onClick={() => setCLicked(true)}
        >
          J'ai déjà un token
        </Button>
      </div>
    )
  }

  return (
    <form
      className="content"
      onSubmit={(event) => {
        onSubmit(inputRef.current?.value || '')
        event.preventDefault()
      }}
    >
      <Button
        className="button-as-link"
        type="button"
        onClick={() => setCLicked(false)}
      >
        {'<'} Retour
      </Button>
      <p>Votre jeton gitlab avec le scope "API"</p>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Input className="input-element" type="text" ref={inputRef} />
        <Button className="button-element" type="submit">
          Ok
        </Button>
      </div>
    </form>
  )
}
